import React, { useEffect, useMemo, useState } from 'react'
import ModulePanel from '../../../ui/ModulePanel/ModulePanel'
import Button from '../../../ui/Button/Button'
import Field from '../../../ui/Field/Field'
import Switch from '../../../ui/Switch/Switch'
import PhotoManager from '../../PhotoManager/PhotoManager'
import SpecificationsBlock from '../../Products/components/ProductsList/ProductsManager/SpecificationsBlock/SpecificationsBlock'
import useLazyFirestoreCatalog from '../UtmBuilder/useLazyFirestoreCatalog'
import loadRelatedProductCatalog from '../../../api/marketing/loadRelatedProductCatalog'
import getFormValues from '../../../utils/newforms/getFormValues'
import {
  GIFT_FUND_SOURCE_CATALOG,
  GIFT_FUND_SOURCE_CUSTOM,
  buildFundGroupSelectOptions,
  buildFundTierSelectOptions,
  buildGiftFundItemForSave,
  generateGiftFundProductCode,
  validateGiftFundItemDraft,
} from '../../../api/marketing/gifts/giftFundHelpers'
import { useGiftsConfig } from './GiftsConfigContext'
import { useGiftsFund } from './GiftsFundContext'
import GiftFundCatalogFields from './GiftFundCatalogFields'
import RelatedProductSearch from '../UtmBuilder/RelatedProductSearch'
import styles from './GiftsFund.module.scss'
import '../UtmBuilder/UtmBuilder.sass'

function GiftsFundItemEditor({ draft, isNew, isSaving = false, onChange, onCancel, onSave }) {
  const { sortedGroups, sortedTiers } = useGiftsConfig()
  const { items: fundItems, catalogAvailability } = useGiftsFund()
  const [error, setError] = useState('')
  const [specificationsSeed, setSpecificationsSeed] = useState(() => draft?.specifications || {})
  const [descriptionSeed, setDescriptionSeed] = useState(() => draft?.description || '')

  useEffect(() => {
    setSpecificationsSeed(draft?.specifications || {})
    setDescriptionSeed(draft?.description || '')
    // Seed editor/spec form only when switching cards. Live draft updates must
    // not reset description HTML, or the text editor cursor jumps on every key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.id])

  useEffect(() => {
    if (draft?.sourceType === GIFT_FUND_SOURCE_CUSTOM) {
      setSpecificationsSeed(draft?.specifications || {})
    }
    // Reseed spec form only when switching type, not on every spec keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft?.sourceType])

  const {
    data: productSearchIndex,
    isLoading: isProductsLoading,
    loadError: productsLoadError,
    load: loadProductCatalog,
  } = useLazyFirestoreCatalog({
    loadCatalog: loadRelatedProductCatalog,
    errorMessage: 'Не вдалося завантажити каталог товарів',
    logLabel: '[GiftsFund] Failed to load products:',
  })

  const groupOptions = useMemo(() => buildFundGroupSelectOptions(sortedGroups), [sortedGroups])
  const tierOptions = useMemo(() => buildFundTierSelectOptions(sortedTiers), [sortedTiers])

  useEffect(() => {
    setError('')
  }, [draft?.id, draft?.sourceType])

  const patch = (partial) => {
    onChange((prev) => ({ ...prev, ...partial }))
  }

  const isCatalog = draft.sourceType === GIFT_FUND_SOURCE_CATALOG
  const isCustom = draft.sourceType === GIFT_FUND_SOURCE_CUSTOM
  const typeLocked = !isNew

  const setSourceType = (nextType) => {
    if (typeLocked || draft.sourceType === nextType) {
      return
    }

    onChange((prev) => {
      if (nextType === GIFT_FUND_SOURCE_CUSTOM) {
        return {
          ...prev,
          sourceType: GIFT_FUND_SOURCE_CUSTOM,
          productCode: prev.productCode || generateGiftFundProductCode(),
        }
      }

      return {
        ...prev,
        sourceType: GIFT_FUND_SOURCE_CATALOG,
      }
    })
  }

  const patchCustomField = (partial) => {
    if (!isCustom) {
      return
    }

    onChange((prev) => ({
      ...prev,
      ...partial,
    }))
  }

  const handleSave = async () => {
    const validation = validateGiftFundItemDraft(draft, {
      rewardGroups: sortedGroups,
      tiers: sortedTiers,
      existingItems: fundItems,
    })

    if (!validation.ok) {
      setError(validation.errors[0] || 'Перевірте поля')
      return
    }

    setError('')
    const payload = buildGiftFundItemForSave(draft)
    const result = await onSave(payload)
    if (result && result.ok === false && result.message) {
      setError(result.message)
    }
  }

  const panelTitle = isNew ? 'Нова позиція фонду' : 'Редагування позиції'

  return (
    <div className={styles.editorRoot}>
      <ModulePanel title={panelTitle}>
        <div className={styles.fields}>
          {isNew ? (
            <div className={styles.typeSwitcherBlock}>
              <div className={styles.sourceTypeLabel}>Тип</div>
              <div className={styles.typeSwitcher} role="radiogroup" aria-label="Тип позиції">
                <button
                  type="button"
                  role="radio"
                  aria-checked={isCatalog}
                  className={[styles.typeOption, isCatalog ? styles.typeOptionActive : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setSourceType(GIFT_FUND_SOURCE_CATALOG)}
                >
                  Із каталогу
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={isCustom}
                  className={[styles.typeOption, isCustom ? styles.typeOptionActive : '']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => setSourceType(GIFT_FUND_SOURCE_CUSTOM)}
                >
                  Власна позиція
                </button>
              </div>
            </div>
          ) : null}

          <div className={styles.switchRow}>
            <Switch
              checked={Boolean(draft.isActive)}
              onChange={(next) => patch({ isActive: next })}
              ariaLabel="Активність позиції"
            />
            <span className={styles.switchLabel}>Активна</span>
          </div>

          <div className={styles.universalRow}>
            <div className={styles.universalField}>
              <Field
                fieldType="select"
                config={{
                  label: 'Група',
                  getOptions: groupOptions,
                }}
                value={draft.rewardGroupId || ''}
                changed={(event) => patch({ rewardGroupId: event.target.value || null })}
                isChangable
              />
            </div>

            <div className={styles.universalField}>
              <Field
                fieldType="select"
                config={{
                  label: 'Доступна зі ступені',
                  getOptions: tierOptions,
                }}
                value={draft.availableFromTierId || ''}
                changed={(event) => patch({ availableFromTierId: event.target.value || null })}
                isChangable
              />
            </div>
          </div>

          {isCatalog ? (
            <div className={styles.typeForm}>
              <GiftFundCatalogFields
                draft={draft}
                onChange={onChange}
                catalogAvailability={catalogAvailability}
                productSearchIndex={productSearchIndex}
                isProductsLoading={isProductsLoading}
                productsLoadError={productsLoadError}
                loadProductCatalog={loadProductCatalog}
                isLoadingProduct={false}
              />
            </div>
          ) : null}

          {isCustom ? (
            <div className={styles.typeForm}>
              <div className={styles.typeColumnFields}>
                <div className={styles.field}>
                  <Field
                    fieldType="input"
                    config={{ label: 'Кількість', type: 'number', min: 0, step: 1 }}
                    value={draft.quantity === 0 || draft.quantity ? String(draft.quantity) : ''}
                    changed={(event) => {
                      const raw = event.target.value
                      if (raw === '') {
                        patchCustomField({ quantity: 0 })
                        return
                      }
                      const next = Number(raw)
                      if (!Number.isInteger(next) || next < 0) {
                        return
                      }
                      patchCustomField({ quantity: next })
                    }}
                    isChangable
                  />
                </div>
                <div className={styles.customMainRow}>
                  <Field
                    fieldType="input"
                    config={{ label: 'Бренд', type: 'text' }}
                    value={draft.brandName || ''}
                    changed={(event) => patchCustomField({ brandName: event.target.value })}
                    isChangable
                  />
                  <Field
                    fieldType="input"
                    config={{ label: 'Назва', type: 'text' }}
                    value={draft.title || ''}
                    changed={(event) => patchCustomField({ title: event.target.value })}
                    isChangable
                  />
                  <Field
                    fieldType="input"
                    config={{ label: 'Обʼєм', type: 'text' }}
                    value={draft.volumeText || ''}
                    changed={(event) => patchCustomField({ volumeText: event.target.value })}
                    isChangable
                  />
                </div>
                <div className={styles.field}>
                  <RelatedProductSearch
                    label="Посилання на товар у каталозі"
                    value={
                      draft.catalogProductId
                        ? {
                            id: draft.catalogProductId,
                            source: draft.catalogSource || 'premium',
                            label: draft.catalogTitle || draft.catalogProductId,
                            metaLabel: draft.catalogBrandName || '',
                            sourceLabel:
                              draft.catalogSource === 'outlet' ? 'Outlet' : 'Premium',
                          }
                        : null
                    }
                    onChange={(product) => {
                      if (!product) {
                        patchCustomField({
                          catalogProductId: null,
                          catalogSource: null,
                          catalogTitle: null,
                          catalogBrandName: null,
                        })
                        return
                      }
                      patchCustomField({
                        catalogProductId: product.id || null,
                        catalogSource: product.source === 'outlet' ? 'outlet' : 'premium',
                        catalogTitle: product.label || product.name || product.id || null,
                        catalogBrandName: product.brandName || null,
                      })
                    }}
                    searchIndex={productSearchIndex || []}
                    filters={{
                      catalogSources: ['premium'],
                      brandIds: [],
                      categoryIds: null,
                      subcategoryIds: null,
                    }}
                    isLoading={isProductsLoading}
                    loadError={productsLoadError}
                    onLoadRequest={loadProductCatalog}
                    canBrowseOnFocus={false}
                  />
                </div>
                <Field
                  key={`description-${draft.id}`}
                  fieldType="texteditor"
                  config={{ label: 'Опис', placeholder: '' }}
                  value={descriptionSeed}
                  initialValue={descriptionSeed}
                  changed={(html) => patchCustomField({ description: typeof html === 'string' ? html : '' })}
                  isChangable
                />
                <div className={styles.detailedDescription}>
                  <p className={styles.sectionLabel}>Детальний опис</p>
                  <SpecificationsBlock
                    key={`specifications-${draft.id}`}
                    formData={specificationsSeed}
                    syncState={(moduleState) => {
                      if (!moduleState) {
                        return
                      }
                      patchCustomField({ specifications: getFormValues({ form: moduleState }) })
                    }}
                  />
                </div>
                <div className={styles.photosBlock}>
                  <p className={styles.sectionLabel}>Фото</p>
                  <PhotoManager
                    key={`photos-${draft.id}`}
                    projectId={draft.id}
                    projectPath="giftFundItems"
                    formData={draft.photos || []}
                    persistDeletes={false}
                    syncState={(photos) => {
                      patchCustomField({ photos })
                    }}
                  />
                </div>
              </div>
            </div>
          ) : null}

          {error ? <p className={styles.error}>{error}</p> : null}
        </div>
      </ModulePanel>

      <div className={styles.footer}>
        <Button
          title="Зберегти"
          theme="solid"
          fill="accent"
          size={32}
          isLoading={isSaving}
          onClick={handleSave}
        />
        <Button title="Скасувати" theme="solid" fill="normal" size={32} onClick={onCancel} />
      </div>
    </div>
  )
}

export default GiftsFundItemEditor
